import type { Column, SortDirection } from '../simple-table'
import type { Medication } from './medication'

import * as Sentry from '@sentry/react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDebounce } from 'react-use'

import http from '../../../lib/http'
import { useAppSelector } from '../../../store'
import { FriendlyDateTime } from '../../shared/Date/friendly-date-time'
import { ErrorToast } from '../../shared/error-toast'
import { Filters } from '../components/filters'
import { Status, statusNameMapper } from '../diagnoses-table/status.enum'
import { useDebouncedFilters } from '../hooks/use-debounced-filters'
import * as Table from '../simple-table'

import styles from './index.module.css'
import { medicationsSchema } from './schema'

type QueryKey = [
  string,
  string | undefined,
  SearchParams
]

interface Response {
  data: Medication[]
  filters: {
    category: string[]
    ndc_code: string[]
    provider_name: string[]
    place_of_service: string[]
    status: string[]
  }
  total_filtered: number
  total_unfiltered: number
  updated_at: string
}

interface SearchParams extends Record<string, unknown>{
  query: string

  filters: {
    category: string[]
    ndc_code: string[]
    provider_name: string[]
    place_of_service: string[]
    status: Status[]
  }

  sort: {
    field: keyof Medication
    direction: SortDirection
  }

  pagination: {
    page: number
    pageSize: number
  }
}

let defaultSearchParams: SearchParams = {
  query: '',

  filters: {
    category: [],
    ndc_code: [],
    provider_name: [],
    place_of_service: [],
    status: [
      Status.New
    ]
  },

  sort: {
    field: 'last_claim_date',
    direction: 'desc'
  },

  pagination: {
    page: 0,
    pageSize: 10
  }
}

function isDefaultSearchParams (params: SearchParams): boolean {
  return JSON.stringify(params) === JSON.stringify(defaultSearchParams)
}

interface MedicationsActionsProps {
  item: Medication
  onRefetch?: () => Promise<unknown> | unknown
  onError?: (message: string) => Promise<unknown> | unknown
}

interface MedicationsActionsState {
  isLoading: boolean
  error?: Error
  currentAction?: 'add' | 'ignore' | 'restore'
}

function MedicationsActions ({ item, onRefetch, onError }: MedicationsActionsProps): JSX.Element | null {
  let resource = useAppSelector(state => state.globalVars?.params?.id)
  let code = item.rx_code
  let status = item.status

  let [ state, setState ] = useState<MedicationsActionsState>({
    isLoading: false
  })

  let handleAction = useCallback((resource: string, code: string, action: NonNullable<MedicationsActionsState['currentAction']>) => {
    setState(state => ({
      ...state,
      isLoading: true,
      currentAction: action
    }))

    http.postRequest(`/patients/${resource}/claims/medications/${action}`, { code })
      .then(() => {
        onRefetch?.()
      })
      .catch((err: unknown) => {
        setState(state => ({
          ...state,
          error: err instanceof Error ? err : new Error('Unknown error')
        }))

        if (err instanceof AxiosError) {
          let { status, data } = err.response ?? {}

          if (status === 404 && data?.message) {
            onError?.(data.message)
          }
        }
      })
      .finally(() => {
        setState(state => ({
          ...state,
          isLoading: false,
          currentAction: undefined
        }))
      })
  }, [ onRefetch, onError ])

  if (!resource || !code) {
    return null
  }

  return (
    <Table.ActionButtonWrapper>
      {status === 'new' && (
        <>
          <Table.ActionButton
            primary
            isLoading={state.isLoading && state.currentAction === 'add'}
            isDisabled={state.isLoading}
            onClick={event => {
              event.preventDefault()
              handleAction(resource as string, code as string, 'add')
            }}
          >
            Add
          </Table.ActionButton>
          <Table.ActionButton
            isLoading={state.isLoading && state.currentAction === 'ignore'}
            isDisabled={state.isLoading}
            onClick={event => {
              event.preventDefault()
              handleAction(resource as string, code as string, 'ignore')
            }}
          >
            Ignore
          </Table.ActionButton>
        </>
      )}

      {status === 'ignored' && (
        <Table.ActionButton
          isLoading={state.isLoading && state.currentAction === 'restore'}
          isDisabled={state.isLoading}
          onClick={event => {
            event.preventDefault()
            handleAction(resource as string, code as string, 'restore')
          }}
        >
          Restore
        </Table.ActionButton>
      )}
    </Table.ActionButtonWrapper>
  )
}

interface MedicationsClaimsTableProps {
  onAction?: () => void
  onMount?: (refetch: () => Promise<unknown> | unknown) => void
}

export function MedicationsClaimsTable ({ onAction, onMount }: MedicationsClaimsTableProps): JSX.Element {
  let [
    debouncedSearchParams,
    searchParams,
    setSearchParams,
    setDebouncedSearchParams
  ] = useDebouncedFilters<SearchParams>(defaultSearchParams)
  let [ searchQuery, setSearchQuery ] = useState('')
  let [ error, setError ] = useState<string | undefined>()
  let resource = useAppSelector(state => state.globalVars?.params?.id)

  let client = useQueryClient()

  const onError = useCallback((message: string): void => {
    setError(message)
    setTimeout(() => setError(undefined), 10000)
  }, [])

  let query = useQuery<Response | undefined, unknown, Response | undefined, QueryKey>({
    queryKey: [
      'claims-history/medications',
      resource,
      debouncedSearchParams
    ],
    queryFn: async function getMedications ({ queryKey }): Promise<Response | undefined> {
      let [ , resource, searchParams ] = queryKey

      let { query, filters, sort, pagination } = searchParams
      let normalizedQuery = query.trim().toLowerCase()

      let urlSearchParams = new URLSearchParams([
        [ 'on', sort.field ],
        [ 'dir', sort.direction ],
        [ 'page', pagination.page.toString(10) ],
        [ 'size', pagination.pageSize.toString(10) ],
        ...filters.ndc_code.map(ndc => [ 'ndc_code', ndc ]),
        ...filters.provider_name.map(provider => [ 'provider_name', provider ]),
        ...filters.place_of_service.map(place => [ 'place_of_service', place ]),
        ...filters.category.map(category => [ 'category', category ]),
        ...filters.status.map(status => [ 'status', status ])
      ])

      if (normalizedQuery.length > 0) {
        urlSearchParams.set('search', normalizedQuery)
      }

      if (!resource) {
        return
      }

      let response: Response = await http.getRequest(`/patients/${resource}/claims/medications?${urlSearchParams.toString()}`)
      let data = medicationsSchema.parse(response.data)

      return {
        ...response,
        data
      }
    },
    enabled: typeof resource === 'string',
    retry: false,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    refetchOnReconnect: true,
    refetchOnWindowFocus: false
  })

  const { refetch } = query

  const onRefetch = useCallback(async () => {
    await client.invalidateQueries({
      predicate ({ queryKey }) {
        return Array.isArray(queryKey) && queryKey[0] === 'claims-history/diagnoses'
      }
    })

    await refetch()
    onAction?.()
  }, [ client, refetch, onAction ])

  useEffect(() => {
    onMount?.(refetch)
  }, [ onMount, refetch ])

  let columns: Array<Column<Medication>> = useMemo(() => [
    {
      name: 'description',
      header: 'Medication',
      isMergeable: true,
      isSortable: true,
      gridArea: '2.3fr',
      getValue ({ description }) {
        if (typeof description !== 'string') {
          return null
        }

        return (
          <Link
            to={`../claims-history?query=${encodeURIComponent(description)}`}
            className={styles.medicationLink}
          >
            <strong>{description}</strong>
          </Link>
        )
      }
    },
    {
      name: 'category',
      header: 'Category',
      isSortable: true,
      getValue: item => item.category
        ?.filter(Boolean)
        .join(', '),
      gridArea: '2fr'
    },
    {
      name: 'ndc_code',
      header: 'NDC',
      isSortable: true,
      getValue: item => item.ndc_code
        ?.filter(Boolean)
        .join(', ')
    },
    {
      name: 'provider_name',
      header: 'Providers',
      isSortable: true,
      getValue: item => item.provider_name
        ?.filter(Boolean)
        .join(', ')
    },
    {
      name: 'place_of_service',
      header: 'Places of Service',
      isSortable: true,
      getValue: item => item.place_of_service
        ?.filter(Boolean)
        .join(', '),
      gridArea: 'minmax(10rem, 1fr)'
    },
    {
      name: 'claims',
      header: (
        <div
          title='Number of Claims'
          style={{
            fontSize: 'inherit',
            fontWeight: 'inherit'
          }}
        >
          Claims
        </div>
      ),
      isSortable: true,
      initialSortDirection: 'desc',
      style: {
        textAlign: 'right'
      },
      headerStyle: {
        display: 'flex',
        justifyContent: 'flex-end'
      }
    },
    {
      name: 'first_claim_date',
      header: (
        <div
          title='First Claim Date'
          style={{
            fontSize: 'inherit',
            fontWeight: 'inherit'
          }}
        >
          First
        </div>
      ),
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (item) {
        if (typeof item.first_claim_date !== 'string') {
          return null
        }

        return Table.formatDate(item.first_claim_date)
      },
      style: {
        textAlign: 'right'
      },
      headerStyle: {
        display: 'flex',
        justifyContent: 'flex-end'
      }
    },
    {
      name: 'last_claim_date',
      header: (
        <div
          title='Last Claim Date'
          style={{
            fontSize: 'inherit',
            fontWeight: 'inherit'
          }}
        >
          Last
        </div>
      ),
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (item) {
        if (typeof item.last_claim_date !== 'string') {
          return null
        }

        return Table.formatDate(item.last_claim_date)
      },
      style: {
        textAlign: 'right'
      },
      headerStyle: {
        display: 'flex',
        justifyContent: 'flex-end'
      }
    },
    {
      name: 'status',
      header: 'Status',
      isSortable: true,
      initialSortDirection: 'asc',
      getValue (item) {
        return typeof item.status === 'string'
          ? statusNameMapper[item.status as Status] ?? '–'
          : '–'
      }
    },
    {
      name: 'actions',
      header: 'Actions',
      headerStyle: {
        display: 'flex',
        justifyContent: 'flex-end'
      },
      getValue (item) {
        return (
          <MedicationsActions
            item={item}
            onRefetch={onRefetch}
            onError={onError}
          />
        )
      }
    }
  ], [ onError, onRefetch ])

  useDebounce(() => {
    setSearchParams(params => ({
      ...params,
      query: searchQuery,
      pagination: {
        ...params.pagination,
        page: 0
      }
    }))
  }, 500, [ searchQuery ])

  let handlePageChange = useCallback((value: number): void => {
    setDebouncedSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: value - 1
      }
    }))
  }, [ setDebouncedSearchParams ])

  let handlePageSizeChange = useCallback((value: number): void => {
    setDebouncedSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0,
        pageSize: value
      }
    }))
  }, [ setDebouncedSearchParams ])

  let handleSortChange = useCallback((field: keyof Medication, direction: SortDirection): void => {
    setSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0
      },
      sort: {
        ...params.sort,
        field,
        direction
      }
    }))
  }, [ setSearchParams ])

  let handleFilterChange = useCallback((name: string, value: Array<string | number | boolean>): void => {
    setSearchParams(params => ({
      ...params,
      pagination: {
        ...params.pagination,
        page: 0
      },
      filters: {
        ...params.filters,
        [name]: value
      }
    }))
  }, [ setSearchParams ])

  let handleFiltersClear = useCallback((): void => {
    setSearchParams(params => ({
      ...params,
      query: '',
      pagination: {
        ...params.pagination,
        page: 0
      },
      filters: {
        category: [],
        ndc_code: [],
        provider_name: [],
        place_of_service: [],
        status: []
      }
    }))
    setSearchQuery('')
  }, [ setSearchParams ])

  let filters = useMemo(() => {
    let keys: Array<keyof SearchParams['filters']> = [
      'provider_name',
      'place_of_service',
      'ndc_code',
      'category',
      'status'
    ]

    return keys.map(key => {
      let options = (query.data?.filters?.[key] ?? [])
        .map((value: string | number) => {
          let label = value

          if (key === 'status') {
            label = statusNameMapper[value as Status] ?? value
          }

          return {
            label: label.toString(),
            value
          }
        })

      return {
        name: key,
        value: searchParams.filters[key],
        options
      }
    })
  }, [ query.data?.filters, searchParams.filters ])

  return (
    <div>
      <Table.Table
        header={(
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
            <h2>
              Claims History | Medications
            </h2>
            {query.status === 'success' && (
              <div>
                <Filters
                  search={searchQuery}
                  onSearchChange={setSearchQuery}
                />
              </div>
            )}
          </div>
        )}
        columns={columns}
        data={query.data?.data ?? []}
        sort={searchParams.sort}
        pagination={{
          ...searchParams.pagination,
          totalFiltered: query.data?.total_filtered,
          totalUnfiltered: query.data?.total_unfiltered,
          extra: query.data?.updated_at
            ? (
              <span>
                Claims information refreshed
                {' '}
                <FriendlyDateTime
                  datetime={query.data.updated_at}
                  diff
                />
              </span>
            )
            : undefined
        }}
        filters={filters}
        error={query.error as Error}
        queryStatus={query.status}
        fetchStatus={query.fetchStatus}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onPageSizeChange={handlePageSizeChange}
        onRefetch={() => {
          if (isDefaultSearchParams(searchParams)) {
            return query.refetch()
          }

          client.invalidateQueries({
            queryKey: [
              'claims-history/medications',
              resource
            ]
          })
            .then(() => {
              setSearchParams(defaultSearchParams)
            })
            .catch(err => {
              console.error(err)
              Sentry.captureException(err)
            })
        }}
      />
      <ErrorToast message={error} />
    </div>
  )
}
